import { AlertSoundId } from '@hmax-order/shared/types';

import { AlertSoundSpec, AlertTone } from './alert-sound.type';

/** Timbre de sino: parciais fora da série harmônica, o que dá o "metal". */
const BELL_PARTIALS = [
  [2, 0.45],
  [2.76, 0.35],
  [5.4, 0.12],
] as const;

/** Timbre suave de carrilhão/campainha: harmônicos baixos e discretos. */
const SOFT_PARTIALS = [
  [2, 0.25],
  [3, 0.08],
] as const;

/** Timbre de marimba: o 4º harmônico curto é o "toc" da baqueta. */
const MARIMBA_PARTIALS = [[4, 0.18]] as const;

/** Repete um padrão de notas deslocado no tempo — o aviso toca duas vezes para não passar despercebido. */
function repeat(tones: readonly AlertTone[], times: number, every: number): AlertTone[] {
  return Array.from({ length: times }, (_, index) => tones.map((tone) => ({ ...tone, startAt: tone.startAt + index * every }))).flat();
}

const CHIME_ARPEGGIO: AlertTone[] = [1047, 1319, 1568, 2093].map((frequency, index) => ({
  frequency,
  startAt: index * 0.16,
  duration: 1.1,
  partials: SOFT_PARTIALS,
}));

const DING_DONG: AlertTone[] = [
  { frequency: 659, startAt: 0, duration: 1.2, partials: SOFT_PARTIALS },
  { frequency: 523, startAt: 0.55, duration: 1.6, partials: SOFT_PARTIALS },
];

const MARIMBA_PHRASE: AlertTone[] = [784, 1047, 1319, 1047, 1568].map((frequency, index) => ({
  frequency,
  startAt: index * 0.15,
  duration: index === 4 ? 0.9 : 0.45,
  partials: MARIMBA_PARTIALS,
}));

const INSISTENT_BEEPS: AlertTone[] = [0, 0.22, 0.44].map((startAt) => ({
  frequency: 988,
  startAt,
  duration: 0.16,
  wave: 'triangle' as const,
}));

/**
 * M2-D69 — o que cada som de aviso toca. As chaves são os ids de `ALERT_SOUND_IDS` (`shared/types`), e o
 * `Record` exaustivo faz o compilador cobrar a síntese de todo id novo.
 *
 * Todos, menos o `classic`, ficam entre 2 e 4 segundos: o feedback de operação foi que o bipe antigo (0,3s)
 * passava despercebido na cozinha.
 */
export const ALERT_SOUND_CATALOG: Record<AlertSoundId, AlertSoundSpec> = {
  chime: { labelKey: 'alert-sound.name.chime', tones: repeat(CHIME_ARPEGGIO, 2, 1.3) },
  bell: {
    labelKey: 'alert-sound.name.bell',
    tones: repeat([{ frequency: 830, startAt: 0, duration: 2.2, partials: BELL_PARTIALS }], 2, 1.1),
  },
  'ding-dong': { labelKey: 'alert-sound.name.ding-dong', tones: repeat(DING_DONG, 2, 1.4) },
  marimba: { labelKey: 'alert-sound.name.marimba', tones: repeat(MARIMBA_PHRASE, 2, 1.2) },
  insistent: { labelKey: 'alert-sound.name.insistent', tones: repeat(INSISTENT_BEEPS, 3, 0.9) },
  // O bipe de antes da M2-F3, para quem se acostumou com ele.
  classic: {
    labelKey: 'alert-sound.name.classic',
    tones: [
      { frequency: 880, startAt: 0, duration: 0.12 },
      { frequency: 1320, startAt: 0.14, duration: 0.18 },
    ],
  },
};

/** Duração total do som, em segundos — a nota que termina por último. */
export function alertSoundDuration(spec: AlertSoundSpec): number {
  return Math.max(...spec.tones.map((tone) => tone.startAt + tone.duration));
}
